import { Outlet, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Header from './components/Header';
import Footer from './components/Footer';
import { ThemeProvider } from './contexts/ThemeContext';
import { disableGA, initGA } from './utils/analytics';
import { useAnalytics } from './hooks/useAnalytics';
import { useAdsEnabled } from './hooks/useAdsEnabled';
import { useSiteConsent } from './hooks/useSiteConsent';
import { installGlobalErrorHandlers } from './utils/errorLogger';
import { applySeoMetadata } from './utils/seo';
import { useLazyRefreshUserProfileQuery } from './slices/usersApiSlice';
import { setCredentials } from './slices/authSlice';
import { DRAFT_ROOM_MODES, getStoredDraftRoomMode, normalizeDraftRoomMode } from './utils/draftRoomMode';

const ADSENSE_CLIENT_ID = String(import.meta.env.VITE_ADSENSE_CLIENT_ID || 'ca-pub-1953524820019168').trim();
const ADSENSE_SCRIPT_SRC =
  `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT_ID}`;
const ADSENSE_SCRIPT_SELECTOR = 'script[data-lr-adsense-loader="true"]';

const App = () => {
  const dispatch = useDispatch();
  const syncedUserIdRef = useRef(null);
  const { userInfo } = useSelector((state) => state.auth);
  const [isAdEligibilityReady, setIsAdEligibilityReady] = useState(() => !userInfo?._id);
  const [refreshUserProfile] = useLazyRefreshUserProfileQuery();
  const { consent } = useSiteConsent();

  // Initialize Google Analytics
  useEffect(() => {
    installGlobalErrorHandlers();
  }, []);

  // Track page views
  useAnalytics();
  const adsEnabled = useAdsEnabled();

  const location = useLocation();
  const draftRouteSearchParams = new URLSearchParams(location.search);
  const draftRouteMode = normalizeDraftRoomMode(
    draftRouteSearchParams.get('view') || getStoredDraftRoomMode()
  );
  const isBasicDraftRoute = location.pathname.startsWith('/draft/') && draftRouteMode === DRAFT_ROOM_MODES.BASIC;

  useEffect(() => {
    applySeoMetadata(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    if (consent.analytics) {
      initGA(true);
      return;
    }

    disableGA();
  }, [consent.analytics]);

  useEffect(() => {
    if (!userInfo?._id) {
      syncedUserIdRef.current = null;
      setIsAdEligibilityReady(true);
      return;
    }

    if (syncedUserIdRef.current === userInfo._id) {
      setIsAdEligibilityReady(true);
      return;
    }

    syncedUserIdRef.current = userInfo._id;
    setIsAdEligibilityReady(false);

    void (async () => {
      try {
        const refreshedUserInfo = await refreshUserProfile().unwrap();
        dispatch(setCredentials({ userInfo: refreshedUserInfo }));
      } catch (error) {
        console.error('Failed to refresh user profile on app boot', error);
      } finally {
        setIsAdEligibilityReady(true);
      }
    })();
  }, [dispatch, refreshUserProfile, userInfo?._id]);

  useEffect(() => {
    const existingScript = document.head.querySelector(ADSENSE_SCRIPT_SELECTOR);

    if (!isAdEligibilityReady || !consent.advertising || !adsEnabled || !ADSENSE_CLIENT_ID) {
      existingScript?.remove();
      delete window.adsbygoogle;
      return;
    }

    if (existingScript) {
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = ADSENSE_SCRIPT_SRC;
    script.crossOrigin = 'anonymous';
    script.dataset.lrAdsenseLoader = 'true';
    document.head.appendChild(script);
  }, [adsEnabled, consent.advertising, isAdEligibilityReady]);

  return (
    <ThemeProvider>
      <div className="app-container">
        <Header />
        <main className={`main-content${isBasicDraftRoute ? ' main-content--basic-draft' : ''}`}>
          <Outlet />
        </main>
        {!isBasicDraftRoute && <Footer />}
        <ToastContainer 
          position="bottom-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored"
        />
      </div>
    </ThemeProvider>
  );
};

export default App;
